import React, { useContext } from "react";
import { useLocation } from "react-router-dom";

import { SidebarInset, SidebarProvider } from "@/ui-components/ui/sidebar";
import { Auth0Provider } from "@auth0/auth0-react";
import { OnboardingManager } from "@/util-components/onboarding/OnboardingManager";
import AppSidebar from "./app-sidebar";
import { Context, ContextProvider } from "./context";
import { GlobalInfoBannerHost } from "./global-info-banner";

export const EXCLUDE_SIDEBAR_PATHS = [
  "/register",
  "/reset",
  "/verification/mail",
  "/vereine/create",
  "/user-not-found",
  "/banned",
  "/logout",
  "/complete-profile",
  "/choose-plan",
  "/billing/success",
];

const InnerLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useContext(Context);
  const location = useLocation();
  
  const isSidebarVisible = !EXCLUDE_SIDEBAR_PATHS.includes(location.pathname) && !!user;

  return (
    <SidebarProvider defaultOpen className="h-full overflow-hidden">
      {isSidebarVisible && <AppSidebar />}
      <SidebarInset className="overflow-hidden flex flex-col">
        {/* Mobile Header Spacer */}
        {isSidebarVisible && <div className="h-16 flex-shrink-0 md:hidden" />}
        <GlobalInfoBannerHost />
        <main className="flex-1 w-full overflow-hidden">{children}</main>
      </SidebarInset>
      {isSidebarVisible && <OnboardingManager />}
    </SidebarProvider>
  );
};

const RootWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <AuthProvider>
      <ContextProvider>
        <InnerLayout>{children}</InnerLayout>
      </ContextProvider>
    </AuthProvider>
  );
};

const AuthProvider = ({ children }: any) => {
  const redirectUri = window.location.origin;
  if (
    !(
      process.env.REACT_APP_AUTH0_DOMAIN &&
      process.env.REACT_APP_AUTH0_ID &&
      process.env.REACT_APP_AUTH0_AUDIENCE &&
      redirectUri
    )
  ) {
    return null;
  }

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_ID}
      cacheLocation="localstorage"
      authorizationParams={{
        redirect_uri: window.location.origin,
        scope: "openid profile email offline_access",
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        ui_locales: "de",
      }}
    >
      {children}
    </Auth0Provider>
  );
};

export default RootWrapper;
